import { useMediaQuery, useTheme } from "@mui/material";
import { motion, type Variants } from "framer-motion";

// Animation variants for first line (depart to icon)
const firstLineVariants: Variants = {
  hidden: {
    pathLength: 0,
  },
  visible: {
    pathLength: 1,
    transition: {
      duration: 1,
      ease: "easeInOut",
    },
  },
};

// Animation variants for second line (icon to arrival)
const secondLineVariants: Variants = {
  hidden: {
    pathLength: 0,
  },
  visible: {
    pathLength: 1,
    transition: {
      duration: 1,
      delay: 1.5,
      ease: "easeInOut",
    },
  },
};

// Dot animation variants
const dotVariants: Variants = {
  hidden: {
    scale: 1,
  },
  pulse: {
    scale: [1, 1.3, 1],
    transition: {
      duration: 0.6,
      delay: 2.5,
      ease: "easeInOut",
    },
  },
};

// Icon pulse animation
const iconPulseVariants: Variants = {
  hidden: {
    scale: 1,
  },
  pulse: {
    scale: [1, 1.2, 1],
    transition: {
      duration: 0.6,
      delay: 1,
      ease: "easeInOut",
    },
  },
};

export function PathDrawing() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const isBreaking = isMdUp || isSmUp;

  // Coordinates for horizontal and vertical layouts
  const layout = !isBreaking
    ? {
        width: "60",
        height: "160", // reduced height for vertical
        viewBox: "0 0 60 160",
        // vertical: top to bottom, compact
        start: { cx: 30, cy: 8 },
        icon: { cx: 30, cy: 80 },
        end: { cx: 30, cy: 150 },
        line1: { x1: 30, y1: 0, x2: 30, y2: 80 },
        line2: { x1: 30, y1: 0, x2: 30, y2: 140 },
      }
    : {
        width: "100%",
        height: "60",
        viewBox: "0 0 400 60",
        // horizontal: left to right
        start: { cx: 20, cy: 30 },
        icon: { cx: 200, cy: 30 },
        end: { cx: 380, cy: 30 },
        line1: { x1: 20, y1: 30, x2: 200, y2: 30 },
        line2: { x1: 200, y1: 30, x2: 380, y2: 30 },
      };

  return (
    <motion.svg
      width={layout.width}
      height={layout.height}
      viewBox={layout.viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      initial="hidden"
      animate="visible"
    >
      {/* Gradient definitions for modern look */}
      <defs>
        <linearGradient
          id="lineGradient"
          gradientUnits="userSpaceOnUse"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="0%"
        >
          {/* Gradient: vibrant pink → light pink → cyan for dark blue backgrounds */}
          <stop offset="0%" stopColor="#ff4fa3" stopOpacity="0.95" />
          <stop offset="50%" stopColor="#ffb6e6" stopOpacity="0.98" />
          <stop offset="100%" stopColor="#00eaff" stopOpacity="0.92" />
        </linearGradient>

        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* First line segment: Depart to Icon */}
      <motion.line
        x1={layout.line1.x1}
        y1={layout.line1.y1}
        x2={layout.line1.x2}
        y2={layout.line1.y2}
        stroke="url(#lineGradient)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="8 8"
        variants={firstLineVariants}
        initial="hidden"
        animate="visible"
      />

      {/* Second line segment: Icon to Arrival */}
      <motion.line
        x1={layout.line2.x1}
        y1={layout.line2.y1}
        x2={layout.line2.x2}
        y2={layout.line2.y2}
        stroke="url(#lineGradient)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="8 8"
        variants={secondLineVariants}
        initial="hidden"
        animate="visible"
      />

      {/* Start dot - pulses at the end */}
      <motion.g initial="hidden" animate="pulse" variants={dotVariants}>
        <circle cx={layout.start.cx} cy={layout.start.cy} r="8" fill="#ff4fa3" />
        <circle cx={layout.start.cx} cy={layout.start.cy} r="6" fill="#ff4fa3" fillOpacity="0.4" />
      </motion.g>

      {/* End dot - pulses at the end */}
      <motion.g initial="hidden" animate="pulse" variants={dotVariants}>
        <circle cx={layout.end.cx} cy={layout.end.cy} r="8" fill="#00eaff" />
        <circle cx={layout.end.cx} cy={layout.end.cy} r="6" fill="#00eaff" fillOpacity="0.4" />
      </motion.g>

      {/* Car icon circle background */}
      <motion.g initial="hidden" animate="pulse" variants={iconPulseVariants}>
        <circle cx={layout.icon.cx} cy={layout.icon.cy} r="18" fill="white" filter="url(#glow)" />
        <circle
          cx={layout.icon.cx}
          cy={layout.icon.cy}
          r="16"
          fill="#f8fafc"
          stroke="#00dcff"
          strokeWidth="2"
        />
      </motion.g>

      {/* Car icon (simplified SVG car) */}
      <motion.g>
        {/* For vertical, rotate and translate the car icon to match the vertical center */}
        <g
          transform={
            !isBreaking ? `translate(${layout.icon.cx - 200},${layout.icon.cy - 30})` : undefined
          }
        >
          <path
            d="M 192 28 L 195 25 L 205 25 L 208 28 L 208 33 L 192 33 Z M 195 25 L 197 23 L 203 23 L 205 25 M 193 30 C 193 30.5 192.5 31 192 31 C 191.5 31 191 30.5 191 30 C 191 29.5 191.5 29 192 29 C 192.5 29 193 29.5 193 30 M 209 30 C 209 30.5 208.5 31 208 31 C 207.5 31 207 30.5 207 30 C 207 29.5 207.5 29 208 29 C 208.5 29 209 29.5 209 30"
            stroke="#00dcff"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </motion.g>
    </motion.svg>
  );
}

export default PathDrawing;
